<script setup lang="ts">
import axios from 'axios';
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import GoogleAddressAutocomplete from 'vue3-google-address-autocomplete';
import { API_URL, GOOGLE_MAPS_API_KEY } from '@idem/common/constantes';

import ModalCropper from './components/modal-cropper.vue';
import { asignarDireccion } from './utils/direccion.utils';

type ActividadIn = {
  nombre: string;
  descripcion: string;
  fecha: string; // YYYY-MM-DD
  hora: string; // HH:mm
  direccion?: string;
  latitud?: string | number; // input as string, converted to number on submit
  longitud?: string | number; // input as string, converted to number on submit
  link?: string;
};

const $router = useRouter();


const apiBaseUrl = API_URL || '/api';

try {
  await axios.get(`${apiBaseUrl}/profile`);
} catch (e: any) {
  $router.push('/login');
}

const form = reactive<ActividadIn>({
  nombre: '',
  descripcion: '',
  fecha: '',
  hora: '',
  direccion: '',
  latitud: '',
  longitud: '',
  link: '',
});

async function submit() {
  try {
    const payload = {
      ...form,
      latitud: form.latitud !== '' ? Number(form.latitud) : undefined,
      longitud: form.longitud !== '' ? Number(form.longitud) : undefined,
    };
    const idActividad = await axios.put(`${apiBaseUrl}/actividades`, payload);
    const archivo = fotoCroppeada.value || foto.value;
    if (archivo) {
      await uploadFile(archivo, idActividad.data);
    }
    alert('Actividad agregada correctamente');
    await $router.push('/actividades/lista');
  } catch (e: any) {
    alert(e?.response?.data?.message || 'No se pudo agregar la actividad');
  }
}


async function uploadFile(file, idActividad) {
  const formData = new FormData();
  formData.append('foto', file);
  await axios.post(`${apiBaseUrl}/actividades/${idActividad}/foto`, formData);
}

function onAddressSelected(direccion) {
  asignarDireccion(direccion, form);
}
const foto = ref<File>();
const fotoCroppeada = ref<File>();
const mostrarCropper = ref(false);
const inputFoto = ref<HTMLInputElement | null>(null);
const previewUrl = ref<string>();

function handleFileChange(event) {
  const input = event.target as HTMLInputElement;
  const archivo = input.files?.[0];
  if (!archivo) return;
  if (archivo.type !== 'image/jpeg' && archivo.type !== 'image/png') {
    alert('Solo se permiten archivos JPEG y PNG');
    return;
  }
  foto.value = archivo;
  mostrarCropper.value = true;
}

function onConfirm(archivo: File) {
  fotoCroppeada.value = archivo;
  mostrarCropper.value = false;
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = URL.createObjectURL(archivo);
}
</script>

<template>
  <section class="mx-auto max-w-2xl px-4 py-8">
    <router-link to="/actividades/lista" class="mb-6 inline-block text-teal-600 hover:underline"
      >← Volver</router-link
    >
    <h1 class="mb-4 text-2xl font-bold text-gray-900">Agregar Actividad</h1>
    <form class="space-y-4" @submit.prevent="submit">
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
        <input v-model="form.nombre" class="w-full rounded border px-3 py-2" required />
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Descripción</label>
        <textarea v-model="form.descripcion" class="w-full rounded border px-3 py-2" required />
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Fecha</label>
          <input
            type="date"
            v-model="form.fecha"
            class="w-full rounded border px-3 py-2"
            required
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Hora</label>
          <input type="time" v-model="form.hora" class="w-full rounded border px-3 py-2" required />
        </div>
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Dirección</label>
        <google-address-autocomplete
          :apiKey="GOOGLE_MAPS_API_KEY || 'NO_API_KEY_PROVIDED'"
          v-model="form.direccion"
          class="w-full rounded border px-3 py-2"
          placeholder="Calle 123, Ciudad"
          @keydown.enter="e => e.preventDefault()"
          @callback="onAddressSelected"
        />
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2" v-show="false">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Latitud</label>
          <input
            v-model="form.latitud"
            inputmode="decimal"
            class="w-full rounded border px-3 py-2"
            placeholder="-33.45"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Longitud</label>
          <input
            v-model="form.longitud"
            inputmode="decimal"
            class="w-full rounded border px-3 py-2"
            placeholder="-70.67"
          />
        </div>
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Link</label>
        <input type="text" v-model="form.link" class="w-full rounded border px-3 py-2" />
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Foto</label>
        <input
          ref="inputFoto"
          type="file"
          data-testid="input-foto"
          class="w-full rounded border px-3 py-2"
          @change="handleFileChange"
        />
        <img
          v-if="previewUrl"
          :src="previewUrl"
          alt=""
          data-testid="preview-foto"
          class="mt-2 h-43 w-full cursor-pointer object-contain"
          @click="inputFoto?.click()"
        />
      </div>
      <modal-cropper v-if="mostrarCropper && foto" :foto="foto" @confirm="onConfirm" />
      <button type="submit" class="rounded bg-teal-600 px-4 py-2 text-white hover:bg-teal-700">
        Guardar
      </button>
    </form>
  </section>
</template>

<style scoped></style>
